import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import type { ApiClient } from '../api/client';
import {
  AppDetailOperationsView,
  GitlabCiPreviewDialog,
  StageCreateDialog,
  UnifiedOpsPreviewDialog
} from '../components/AppDetailComponents';
import { type JobState } from '../components/BackgroundJobButton';
import { AppDetailSettingsView } from '../components/AppDetailSettingsView';
import { AppExecutorPolicyPanel } from '../components/AppExecutorPolicyPanel';
import { StageMigrationPanel } from '../components/StageMigrationPanel';
import ErrorBanner from '../components/ErrorBanner';
import GitlabUpdatesNotice from '../components/GitlabUpdatesNotice';
import { DEFAULT_AUTOFIX_TEMPLATE, DEFAULT_TOOL_SCOPES, TOOL_SCOPES } from '../constants';
import { useGitlabUpdates } from '../hooks/useGitlabUpdates';
import type {
  AppDetail,
  AppAutopilotSettings,
  AppItem,
  AppStage,
  AppStageSecretExternalSource,
  AppStageSecret,
  AppStageSecretsResponse,
  AutopilotMode,
  Conversation,
  ConversationGitlabInfo,
  CreateAppStageSecretRequest,
  DeployKeyStatus,
  DeprovisionResult,
  DnsDomainsResponse,
  GitLabCiPreviewResponse,
  IdentityHealthResponse,
  IdentityServiceSummary,
  ImportDeployedSecretsResponse,
  ListIdentityServicesResponse,
  MoveAppStageSecretRequest,
  OpsOverview,
  OpsHistoryResponse,
  OpsRevertRequest,
  OpsRevertResponse,
  Pipeline,
  PipelineWaitSettings,
  PipelineJob,
  ReconcileResult,
  Server,
  StageDnsEnsureResponse,
  StageDeleteResponse,
  StageListResponse,
  StageResponse,
  UnifiedOpsApplyRequest,
  UnifiedOpsApplyResponse,
  UnifiedOpsPreviewResponse,
  UpdateAppStageSecretRequest,
  WorkerNode,
} from '../types';
import { getAppGitlabRuntimeResource } from '../utils/appGitlabRuntimeResource';
import { reportErrorLocal } from '../utils/errors';
import { useLiveResource } from '../utils/liveResource';
import './AppDetailPage.lane45.css';

function getMissingSecretKeys(preview: UnifiedOpsPreviewResponse | null): string[] {
  if (!preview) {
    return [];
  }
  if (Array.isArray(preview.missing_secrets) && preview.missing_secrets.length > 0) {
    return Array.from(new Set(preview.missing_secrets));
  }
  const inferred = (preview.secrets_info ?? [])
    .filter((secret) => !secret.has_value)
    .map((secret) => secret.key);
  return Array.from(new Set(inferred));
}

export default function AppDetailPage({
  apiClient,
  setError,
  showMembersLink = false
}: {
  apiClient: ApiClient;
  setError: (message: string | null) => void;
  showMembersLink?: boolean;
}) {
  const navigate = useNavigate();
  const params = useParams();
  const appId = params.appId ?? '';
  const appGitlabRuntime = useMemo(
    () => getAppGitlabRuntimeResource(apiClient, appId),
    [apiClient, appId]
  );
  const appGitlabRuntimeSnapshot = useLiveResource(appGitlabRuntime.live);
  const pipelines = appGitlabRuntimeSnapshot.data?.pipelines ?? [];
  const hookStatus = appGitlabRuntimeSnapshot.data?.hookStatus ?? null;
  const pipelineJobs = appGitlabRuntimeSnapshot.data?.pipelineJobs ?? {};
  const [detail, setDetail] = useState<AppDetail | null>(null);
  const [pipelineLimit, setPipelineLimit] = useState(5);
  const [pipelineJobsOpen, setPipelineJobsOpen] = useState<Record<number, boolean>>({});
  const [pipelineJobsLoading, setPipelineJobsLoading] = useState<Record<number, boolean>>({});
  const [pipelineError, setPipelineError] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [gitlabRefreshBusy, setGitlabRefreshBusy] = useState(false);
  const [availableApps, setAvailableApps] = useState<AppItem[]>([]);
  const [servers, setServers] = useState<Server[]>([]);
  const [workerNodes, setWorkerNodes] = useState<WorkerNode[]>([]);
  const [dnsDomains, setDnsDomains] = useState<string[]>([]);
  const [dnsDefault, setDnsDefault] = useState<string | null>(null);
  const [deployResult, setDeployResult] = useState<string | null>(null);
  const [restartResult, setRestartResult] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [autofixEnabled, setAutofixEnabled] = useState(false);
  const [autofixFeatureBranches, setAutofixFeatureBranches] = useState(false);
  const [autoMrFeedback, setAutoMrFeedback] = useState(false);
  const [mrFeedbackResult, setMrFeedbackResult] = useState<string | null>(null);
  const [hookBusy, setHookBusy] = useState(false);
  const [autofixPrompt, setAutofixPrompt] = useState(DEFAULT_AUTOFIX_TEMPLATE);
  const [autofixResult, setAutofixResult] = useState<string | null>(null);
  const [appAutopilotMode, setAppAutopilotMode] = useState<AutopilotMode>('semi');
  const [appEnablePushReminder, setAppEnablePushReminder] = useState(false);
  const [appTemplateBuildFailed, setAppTemplateBuildFailed] = useState('');
  const [appTemplateMergeConflict, setAppTemplateMergeConflict] = useState('');
  const [appTemplateReadySemi, setAppTemplateReadySemi] = useState('');
  const [appTemplateReadyFull, setAppTemplateReadyFull] = useState('');
  const [appAutopilotResult, setAppAutopilotResult] = useState<string | null>(null);
  const [pipelineWaitAllowed, setPipelineWaitAllowed] = useState(false);
  const [pipelineWaitBusy, setPipelineWaitBusy] = useState(false);
  const [pipelineWaitResult, setPipelineWaitResult] = useState<string | null>(null);
  const [selectedContainer, setSelectedContainer] = useState('');
  const [deployWaitBusyJobs, setDeployWaitBusyJobs] = useState<Record<string, boolean>>({});
  const [redeployBusyId, setRedeployBusyId] = useState<number | null>(null);
  const [deployKeyStatus, setDeployKeyStatus] = useState<DeployKeyStatus | null>(null);
  const [deployKeyBusy, setDeployKeyBusy] = useState(false);
  const [selfBusy, setSelfBusy] = useState(false);
  const [selfResult, setSelfResult] = useState<string | null>(null);
  const [templateBusy, setTemplateBusy] = useState(false);
  const [templateResult, setTemplateResult] = useState<string | null>(null);
  const [infraHelperBusy, setInfraHelperBusy] = useState(false);
  const [infraHelperResult, setInfraHelperResult] = useState<string | null>(null);
  const [infraCatalogBusy, setInfraCatalogBusy] = useState(false);
  const [targetBusy, setTargetBusy] = useState(false);
  const [targetResult, setTargetResult] = useState<string | null>(null);
  const [targetError, setTargetError] = useState<string | null>(null);
  const [opsOverview, setOpsOverview] = useState<OpsOverview | null>(null);
  const [opsBusyKey, setOpsBusyKey] = useState<string | null>(null);
  const [opsResult, setOpsResult] = useState<string | null>(null);
  const [opsHistory, setOpsHistory] = useState<OpsHistoryResponse | null>(null);
  const [opsHistoryBusy, setOpsHistoryBusy] = useState(false);
  const [opsHistoryResult, setOpsHistoryResult] = useState<string | null>(null);
  const [opsRevertBusyCommit, setOpsRevertBusyCommit] = useState<string | null>(null);
  const [backgroundJobResult, setBackgroundJobResult] = useState<{ message: string; job?: JobState } | null>(null);
  const [stages, setStages] = useState<AppStage[]>([]);
  const [stageBusy, setStageBusy] = useState(false);
  const [stageResult, setStageResult] = useState<string | null>(null);
  const [stageError, setStageError] = useState<string | null>(null);
  const [stageCreateDialogOpen, setStageCreateDialogOpen] = useState(false);
  const [activeView, setActiveView] = useState<'operations' | 'settings'>('operations');
  const [selectedOpsStageId, setSelectedOpsStageId] = useState<string>('production');
  const [selectedSettingsStageId, setSelectedSettingsStageId] = useState<string>('general');
  const [gitlabCiPreview, setGitlabCiPreview] = useState<{ original: string | null; updated: string } | null>(null);
  const [gitlabCiInstructions, setGitlabCiInstructions] = useState<string | null>(null);
  const [gitlabCiBusy, setGitlabCiBusy] = useState(false);
  const [gitlabCiPreviewOpen, setGitlabCiPreviewOpen] = useState(false);
  // Stage secrets state
  const [stageSecrets, setStageSecrets] = useState<AppStageSecret[]>([]);
  const [stageSecretsBusy, setStageSecretsBusy] = useState(false);
  const [stageSecretsResult, setStageSecretsResult] = useState<string | null>(null);
  const [newSecretKey, setNewSecretKey] = useState('');
  const [newSecretValue, setNewSecretValue] = useState('');
  const [newSecretSourceType, setNewSecretSourceType] = useState<'manual' | 'external'>('manual');
  const [newExternalAppId, setNewExternalAppId] = useState('');
  const [newExternalStageMode, setNewExternalStageMode] = useState<'fixed' | 'production' | 'same_stage'>('fixed');
  const [newExternalStageId, setNewExternalStageId] = useState('');
  const [newExternalSecretKey, setNewExternalSecretKey] = useState('');
  const [newSecretType, setNewSecretType] = useState<'env' | 'file'>('env');
  const [newSecretDescription, setNewSecretDescription] = useState('');
  const [editingSecretId, setEditingSecretId] = useState<string | null>(null);
  const [editSecretValue, setEditSecretValue] = useState('');
  const [editSecretSourceType, setEditSecretSourceType] = useState<'manual' | 'external'>('manual');
  const [editExternalAppId, setEditExternalAppId] = useState('');
  const [editExternalStageMode, setEditExternalStageMode] = useState<'fixed' | 'production' | 'same_stage'>('fixed');
  const [editExternalStageId, setEditExternalStageId] = useState('');
  const [editExternalSecretKey, setEditExternalSecretKey] = useState('');
  // Unified preview state
  const [unifiedPreview, setUnifiedPreview] = useState<UnifiedOpsPreviewResponse | null>(null);
  const [unifiedPreviewBusy, setUnifiedPreviewBusy] = useState(false);
  const [unifiedPreviewOpen, setUnifiedPreviewOpen] = useState(false);
  const [unifiedApplyBusy, setUnifiedApplyBusy] = useState(false);
  const [selectedPreviewFiles, setSelectedPreviewFiles] = useState<Set<string>>(new Set());

  // Identity services state
  const [identityServices, setIdentityServices] = useState<IdentityServiceSummary[]>([]);
  const [identityServicesBusy, setIdentityServicesBusy] = useState(false);
  const [identityResult, setIdentityResult] = useState<string | null>(null);
  const [ymlIdConfigured, setYmlIdConfigured] = useState(false);
  const [identityHealth, setIdentityHealth] = useState<IdentityHealthResponse | null>(null);
  const [identityHealthBusy, setIdentityHealthBusy] = useState(false);

  const unifiedMissingSecretKeys = useMemo(
    () => getMissingSecretKeys(unifiedPreview),
    [unifiedPreview]
  );
  const hasUnifiedMissingSecrets = unifiedMissingSecretKeys.length > 0;

  useEffect(() => {
    if (!appGitlabRuntimeSnapshot.error) {
      return;
    }
    setPipelineError(appGitlabRuntimeSnapshot.error);
  }, [appGitlabRuntimeSnapshot.error]);

  const stageTabs = useMemo(() => {
    const defaultStage = stages.find((stage) => stage.is_default) ?? null;
    const additionalStages = stages
      .filter((stage) => !stage.is_default)
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
    return [
      {
        id: 'production',
        label: defaultStage?.name ?? 'Production',
        stage: defaultStage
      },
      ...additionalStages.map((stage) => ({
        id: stage.id,
        label: stage.name,
        stage
      }))
    ];
  }, [stages]);

  const settingsScopeTabs = useMemo(
    () => [{ id: 'general', label: 'General' }, ...stageTabs, { id: '__add_stage__', label: '+ Stage' }],
    [stageTabs]
  );

  const resolveSecretScopeSelection = useCallback(() => {
    if (selectedSettingsStageId === 'general') {
      return { scope: 'general' as const, stageId: undefined as string | undefined };
    }
    const selectedStage = stageTabs.find((stage) => stage.id === selectedSettingsStageId)?.stage ?? null;
    if (!selectedStage) {
      return { scope: 'general' as const, stageId: undefined as string | undefined };
    }
    return { scope: 'stage' as const, stageId: selectedStage.id };
  }, [selectedSettingsStageId, stageTabs]);

  useEffect(() => {
    const availableOpsIds = new Set(stageTabs.map((stage) => stage.id));
    if (!availableOpsIds.has(selectedOpsStageId)) {
      setSelectedOpsStageId(stageTabs[0]?.id ?? 'production');
    }
    const availableSettingsIds = new Set(
      settingsScopeTabs.filter((tab) => tab.id !== '__add_stage__').map((tab) => tab.id)
    );
    if (!availableSettingsIds.has(selectedSettingsStageId)) {
      setSelectedSettingsStageId('general');
    }
  }, [stageTabs, settingsScopeTabs, selectedOpsStageId, selectedSettingsStageId]);

  // Subscribe to GitLab updates via WebSocket
  const conversationIdsForUpdates = useMemo(
    () => conversations.slice(0, 50).map((c) => c.id),
    [conversations]
  );

  const handleGitlabUpdate = useCallback(
    (conversationId: string, gitlab: ConversationGitlabInfo | null) => {
      setConversations((prev) =>
        prev.map((c) => (c.id === conversationId ? { ...c, gitlab } : c))
      );
    },
    []
  );

  const gitlabUpdates = useGitlabUpdates({
    apiClient,
    conversationIds: conversationIdsForUpdates,
    onUpdate: handleGitlabUpdate,
    enabled: conversations.length > 0
  });

  const loadDetail = useCallback(async () => {
    try {
      const data = await apiClient.get<AppDetail>(`/api/apps/${appId}`);
      setDetail(data);
      const autofix = data.autofix;
      setAutofixEnabled(autofix?.enabled ?? false);
      const featureBranches =
        !(autofix?.only_default_branch ?? true) || (autofix?.create_merge_request ?? false);
      setAutofixFeatureBranches(featureBranches);
      setAutofixPrompt(autofix?.prompt_template ?? DEFAULT_AUTOFIX_TEMPLATE);
      setAutoMrFeedback(data.app.auto_mr_feedback ?? false);
    } catch (err) {
      reportErrorLocal('Failed to load app', err, setError, setDetailError);
    }
  }, [apiClient, appId, setError]);

  const loadPipelines = useCallback(async (limitOverride?: number) => {
    try {
      const limit = limitOverride ?? pipelineLimit;
      appGitlabRuntime.setPipelineLimit(limit);
      const next = await appGitlabRuntime.live.refresh('manual-pipelines');
      if (next.error) {
        throw new Error(next.error);
      }
      setPipelineError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setPipelineError(`Pipelines unavailable: ${message}`);
    }
  }, [appGitlabRuntime, pipelineLimit]);

  async function loadPipelineJobs(pipelineId: number) {
    if (pipelineJobsLoading[pipelineId]) {
      return;
    }
    setPipelineJobsLoading((prev) => ({ ...prev, [pipelineId]: true }));
    try {
      await appGitlabRuntime.watchPipelineJobs(pipelineId, true);
      const first = appGitlabRuntime.live.getSnapshot();
      if (!first.data?.pipelineJobs[pipelineId]) {
        await appGitlabRuntime.refresh('pipeline-jobs-bootstrap');
      }
      const next = appGitlabRuntime.live.getSnapshot();
      if (next.error) {
        throw new Error(next.error);
      }
    } catch (err) {
      reportErrorLocal('Failed to load pipeline jobs', err, setError, setDetailError);
    } finally {
      setPipelineJobsLoading((prev) => ({ ...prev, [pipelineId]: false }));
    }
  }

  const loadApps = useCallback(async () => {
    try {
      const data = await apiClient.get<AppItem[]>('/api/apps');
      setAvailableApps(data);
    } catch (err) {
      reportErrorLocal('Failed to load apps', err, setError, setDetailError);
    }
  }, [apiClient, setError]);

  function loadMorePipelines() {
    const next = pipelineLimit + 5;
    setPipelineLimit(next);
    void loadPipelines(next);
  }

  async function loadHookStatus() {
    try {
      const next = await appGitlabRuntime.live.refresh('manual-hook-status');
      if (next.error) {
        throw new Error(next.error);
      }
    } catch (err) {
      reportErrorLocal('Failed to check GitLab hook', err, setError, setDetailError);
    }
  }

  const loadAppAutopilot = useCallback(async () => {
    try {
      const data = await apiClient.get<AppAutopilotSettings>(`/api/apps/${appId}/autopilot`);
      setAppAutopilotMode(data.default_mode ?? 'semi');
      setAppEnablePushReminder(data.enable_push_reminder ?? false);
      setAppTemplateBuildFailed(data.template_build_failed ?? '');
      setAppTemplateMergeConflict(data.template_merge_conflict ?? '');
      setAppTemplateReadySemi(data.template_ready_semi ?? '');
      setAppTemplateReadyFull(data.template_ready_full ?? '');
    } catch (err) {
      reportErrorLocal('Failed to load app autopilot settings', err, setError, setDetailError);
    }
  }, [apiClient, appId, setError]);

  const loadPipelineWaitSettings = useCallback(async () => {
    try {
      const data = await apiClient.get<PipelineWaitSettings>(
        `/api/apps/${appId}/pipeline-wait-settings`
      );
      setPipelineWaitAllowed(data.allow_agent_pipeline_waits);
    } catch (err) {
      reportErrorLocal('Failed to load pipeline wait settings', err, setError, setDetailError);
    }
  }, [apiClient, appId, setError]);

  const loadDeployKeyStatus = useCallback(async () => {
    try {
      const data = await apiClient.get<DeployKeyStatus>(`/api/apps/${appId}/gitlab/deploy-key`);
      setDeployKeyStatus(data);
    } catch (err) {
      setDeployKeyStatus(null);
      reportErrorLocal('Failed to check GitLab deploy key', err, setError, setDetailError);
    }
  }, [apiClient, appId, setError]);

  const loadOps = useCallback(async () => {
    try {
      const data = await apiClient.get<OpsOverview>(`/api/apps/${appId}/ops`);
      setOpsOverview(data);
    } catch (err) {
      setOpsOverview(null);
      reportErrorLocal('Failed to load ops metadata', err, setError, setDetailError);
    }
  }, [apiClient, appId, setError]);

  const loadOpsHistory = useCallback(async (stageIdOverride?: string) => {
    setOpsHistoryBusy(true);
    try {
      const effectiveStageId = stageIdOverride ?? selectedOpsStageId;
      const selectedStage = stageTabs.find((stage) => stage.id === effectiveStageId)?.stage ?? null;
      const stageId = selectedStage?.id;
      const params = stageId ? `?stage_id=${stageId}` : '';
      const data = await apiClient.get<OpsHistoryResponse>(`/api/apps/${appId}/ops/history${params}`);
      setOpsHistory(data);
    } catch (err) {
      setOpsHistory(null);
      const message = err instanceof Error ? err.message : String(err);
      setOpsHistoryResult(`Config history unavailable: ${message}`);
    } finally {
      setOpsHistoryBusy(false);
    }
  }, [apiClient, appId, selectedOpsStageId, stageTabs]);

  const loadUnifiedPreview = useCallback(async () => {
    setUnifiedPreviewBusy(true);
    setUnifiedPreview(null);
    try {
      const selectedStage = stageTabs.find((stage) => stage.id === selectedOpsStageId)?.stage ?? null;
      const stageId = selectedStage?.id;
      const params = stageId ? `?stage_id=${stageId}` : '';
      const data = await apiClient.get<UnifiedOpsPreviewResponse>(`/api/apps/${appId}/ops/preview${params}`);
      const normalizedResponse: UnifiedOpsPreviewResponse = {
        ...data,
        files: data.files ?? [],
        stage_id: data.stage_id ?? stageId ?? null,
        stage_name: data.stage_name ?? selectedStage?.name ?? 'production',
      };

      setUnifiedPreview(normalizedResponse);
      setSelectedPreviewFiles(new Set((normalizedResponse.files ?? []).map((f) => f.path)));
      setUnifiedPreviewOpen(true);
    } catch (err) {
      reportErrorLocal('Failed to load unified preview', err, setError, setDetailError);
    } finally {
      setUnifiedPreviewBusy(false);
    }
  }, [apiClient, appId, selectedOpsStageId, setError, stageTabs]);

  const revertOpsCommit = useCallback(async (commit: string) => {
    if (!commit || opsRevertBusyCommit) {
      return;
    }
    const selectedStage = stageTabs.find((stage) => stage.id === selectedOpsStageId)?.stage ?? null;
    const stageId = selectedStage?.id;
    if (!window.confirm(`Restore this app configuration to commit ${commit.slice(0, 8)}? This creates a new commit.`)) {
      return;
    }
    setOpsRevertBusyCommit(commit);
    setOpsHistoryResult(null);
    try {
      const payload: OpsRevertRequest = {
        commit,
        stage_id: stageId,
      };
      const result = await apiClient.post<OpsRevertResponse>(`/api/apps/${appId}/ops/revert`, payload);
      setOpsHistoryResult(result.message);
      await loadOpsHistory(selectedOpsStageId);
      if (unifiedPreviewOpen) {
        await loadUnifiedPreview();
      }
    } catch (err) {
      reportErrorLocal('Failed to restore config commit', err, setError, setDetailError);
      setOpsHistoryResult('Failed to restore config commit');
    } finally {
      setOpsRevertBusyCommit(null);
    }
  }, [
    apiClient,
    appId,
    loadOpsHistory,
    loadUnifiedPreview,
    opsRevertBusyCommit,
    selectedOpsStageId,
    setError,
    stageTabs,
    unifiedPreviewOpen,
  ]);

  const loadStageSecrets = useCallback(async () => {
    setStageSecretsBusy(true);
    try {
      const { stageId } = resolveSecretScopeSelection();
      const params = stageId ? `?stage_id=${stageId}` : '';
      const data = await apiClient.get<AppStageSecretsResponse>(`/api/apps/${appId}/secrets${params}`);
      setStageSecrets(data.secrets);
    } catch (err) {
      setStageSecrets([]);
      reportErrorLocal('Failed to load stage secrets', err, setError, setDetailError);
    } finally {
      setStageSecretsBusy(false);
    }
  }, [apiClient, appId, resolveSecretScopeSelection, setError]);

  function buildExternalSecretSource(
    appIdValue: string,
    stageMode: 'fixed' | 'production' | 'same_stage',
    stageIdValue: string,
    secretKey: string
  ): AppStageSecretExternalSource | null {
    if (!appIdValue || !secretKey.trim()) {
      return null;
    }
    return {
      app_id: appIdValue,
      stage_id: stageMode === 'fixed' && stageIdValue ? stageIdValue : null,
      stage_mode: stageMode,
      secret_key: secretKey.trim(),
    };
  }

  async function createStageSecret() {
    const manifestSpec =
      opsOverview?.secrets.find((secret) => secret.key === (newSecretKey.trim() || effectiveNewSecretKey)) ?? null;
    const secretKey = (newSecretKey.trim() || effectiveNewSecretKey || '').trim();
    if (!secretKey) {
      setStageSecretsResult('Key is required');
      return;
    }
    const externalSource =
      newSecretSourceType === 'external'
        ? buildExternalSecretSource(
            newExternalAppId,
            newExternalStageMode,
            newExternalStageId,
            newExternalSecretKey
          )
        : null;
    if (
      newSecretSourceType === 'manual' &&
      !newSecretValue.trim() &&
      manifestSpec?.required !== false
    ) {
      setStageSecretsResult('Value is required');
      return;
    }
    if (newSecretSourceType === 'external' && !externalSource) {
      setStageSecretsResult('External app and secret key are required');
      return;
    }
    const secretType = (manifestSpec?.secret_type ?? newSecretType ?? 'env') as 'env' | 'file';
    const filePath = (manifestSpec?.file_path || '').trim();
    if (secretType === 'file' && !filePath) {
      setStageSecretsResult('File path must be configured in deploy/ops.yaml for file-type secrets');
      return;
    }
    setStageSecretsBusy(true);
    setStageSecretsResult(null);
    try {
      const { stageId } = resolveSecretScopeSelection();
      const payload: CreateAppStageSecretRequest = {
        key: secretKey,
        value: newSecretSourceType === 'manual' ? newSecretValue : null,
        stage_id: stageId,
        secret_type: secretType,
        description: newSecretDescription.trim() || undefined,
        source_type: newSecretSourceType,
        external_source: externalSource,
      };
      await apiClient.post(`/api/apps/${appId}/secrets`, payload);
      setNewSecretKey('');
      setNewSecretValue('');
      setNewSecretSourceType('manual');
      setNewExternalAppId('');
      setNewExternalStageMode('fixed');
      setNewExternalStageId('');
      setNewExternalSecretKey('');
      setNewSecretDescription('');
      setStageSecretsResult('Secret created');
      await loadStageSecrets();
    } catch (err) {
      reportErrorLocal('Failed to create secret', err, setError, setDetailError);
      setStageSecretsResult('Failed to create secret');
    } finally {
      setStageSecretsBusy(false);
    }
  }

  async function updateStageSecret(secretId: string) {
    const externalSource =
      editSecretSourceType === 'external'
        ? buildExternalSecretSource(
            editExternalAppId,
            editExternalStageMode,
            editExternalStageId,
            editExternalSecretKey
          )
        : null;
    const currentSecret = stageSecrets.find((secret) => secret.id === secretId) ?? null;
    if (editSecretSourceType === 'manual' && currentSecret?.source_type === 'external' && !editSecretValue.trim()) {
      setStageSecretsResult('Value is required when changing an external secret to manual');
      return;
    }
    if (editSecretSourceType === 'external' && !externalSource) {
      setStageSecretsResult('External app and secret key are required');
      return;
    }
    setStageSecretsBusy(true);
    setStageSecretsResult(null);
    try {
      const payload: UpdateAppStageSecretRequest = {
        value: editSecretSourceType === 'manual' ? editSecretValue || undefined : null,
        source_type: editSecretSourceType,
        external_source: externalSource,
      };
      await apiClient.put(`/api/apps/${appId}/secrets/${secretId}`, payload);
      setEditingSecretId(null);
      setEditSecretValue('');
      setEditSecretSourceType('manual');
      setEditExternalAppId('');
      setEditExternalStageMode('fixed');
      setEditExternalStageId('');
      setEditExternalSecretKey('');
      setStageSecretsResult('Secret updated');
      await loadStageSecrets();
    } catch (err) {
      reportErrorLocal('Failed to update secret', err, setError, setDetailError);
      setStageSecretsResult('Failed to update secret');
    } finally {
      setStageSecretsBusy(false);
    }
  }

  async function deleteStageSecret(secretId: string) {
    if (!window.confirm('Delete this secret?')) {
      return;
    }
    setStageSecretsBusy(true);
    setStageSecretsResult(null);
    try {
      await apiClient.del(`/api/apps/${appId}/secrets/${secretId}`);
      setStageSecretsResult('Secret deleted');
      await loadStageSecrets();
    } catch (err) {
      reportErrorLocal('Failed to delete secret', err, setError, setDetailError);
      setStageSecretsResult('Failed to delete secret');
    } finally {
      setStageSecretsBusy(false);
    }
  }

  async function applyUnifiedPreview() {
    if (!unifiedPreview || selectedPreviewFiles.size === 0) {
      return;
    }
    if (hasUnifiedMissingSecrets) {
      setStageSecretsResult(
        `Cannot apply while required secrets are missing: ${unifiedMissingSecretKeys.join(', ')}`
      );
      return;
    }
    setUnifiedApplyBusy(true);
    try {
      // Apply the exact stage the backend rendered. The selection can change
      // while the preview dialog is open, and applying the current tab would
      // otherwise send a different stage than the files/secrets preview.
      const stageId = unifiedPreview.stage_id ?? null;
      const payload: UnifiedOpsApplyRequest = {
        files: Array.from(selectedPreviewFiles),
        stage_id: stageId,
        restore_backup_id: unifiedPreview.restore_lineage,
      };
      const result = await apiClient.post<UnifiedOpsApplyResponse>(`/api/apps/${appId}/ops/apply`, payload);
      setUnifiedPreviewOpen(false);
      if (result.errors && Object.keys(result.errors).length > 0) {
        const errorMessages = Object.entries(result.errors).map(([path, err]) => `${path}: ${err}`).join('; ');
        setStageSecretsResult(`Some files failed: ${errorMessages}`);
      } else {
        setStageSecretsResult(`Applied ${result.applied.length} file(s)`);
      }
    } catch (err) {
      reportErrorLocal('Failed to apply files', err, setError, setDetailError);
    } finally {
      setUnifiedApplyBusy(false);
    }
  }

  async function generateMissingUnifiedSecrets() {
    const stageId = unifiedPreview?.stage_id ?? null;
    try {
      await apiClient.post(`/api/apps/${appId}/secrets/generate-missing`, {
        stage_id: stageId,
      });
      await loadStageSecrets();
      await loadUnifiedPreview();
    } catch (err) {
      reportErrorLocal('Failed to generate missing secrets', err, setError, setDetailError);
      setStageSecretsResult(
        `Failed to generate missing secrets: ${err instanceof Error ? err.message : String(err)}`
      );
    }
  }

  async function importDeployedSecrets(target: 'stage' | 'general') {
    const stageId = unifiedPreview?.stage_id ?? null;
    const targetStageId = target === 'stage' ? stageId : null;
    if (target === 'stage' && !targetStageId) {
      setStageSecretsResult('Select a stage before importing deployed secrets into a stage scope');
      return;
    }
    try {
      const result = await apiClient.post<ImportDeployedSecretsResponse>(
        `/api/apps/${appId}/secrets/import-deployed`,
        {
          stage_id: stageId,
          target_stage_id: targetStageId,
          target_scope: target,
        }
      );
      const targetLabel = result.target_stage_id ? 'stage' : 'general';
      const parts = [`Imported ${result.imported.length} deployed secret(s) to ${targetLabel}`];
      if (result.already_set.length > 0) {
        parts.push(`${result.already_set.length} already set`);
      }
      if (result.not_found.length > 0) {
        parts.push(`${result.not_found.length} not found in deployed file`);
      }
      if (result.skipped_empty.length > 0) {
        parts.push(`${result.skipped_empty.length} empty`);
      }
      if (result.skipped_non_env.length > 0) {
        parts.push(`${result.skipped_non_env.length} file secret(s) skipped`);
      }
      setStageSecretsResult(parts.join('; '));
      await loadStageSecrets();
      await loadUnifiedPreview();
    } catch (err) {
      reportErrorLocal('Failed to import deployed secrets', err, setError, setDetailError);
      setStageSecretsResult(
        `Failed to import deployed secrets: ${err instanceof Error ? err.message : String(err)}`
      );
    }
  }

  async function moveStageSecret(secretId: string, targetStageId: string | null, overwrite = false) {
    setStageSecretsBusy(true);
    setStageSecretsResult(null);
    try {
      const payload: MoveAppStageSecretRequest = {
        target_stage_id: targetStageId,
        overwrite,
      };
      await apiClient.post(`/api/apps/${appId}/secrets/${secretId}/move`, payload);
      setEditingSecretId(null);
      setStageSecretsResult(targetStageId ? 'Secret moved to stage' : 'Secret moved to General');
      await loadStageSecrets();
      if (unifiedPreviewOpen) {
        await loadUnifiedPreview();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (
        !overwrite &&
        message.includes('destination scope already has a secret with this key') &&
        window.confirm('The destination already has this secret key. Move anyway and overwrite it?')
      ) {
        await moveStageSecret(secretId, targetStageId, true);
        return;
      }
      reportErrorLocal('Failed to move secret', err, setError, setDetailError);
      setStageSecretsResult(`Failed to move secret: ${message}`);
    } finally {
      setStageSecretsBusy(false);
    }
  }

  async function generateUnifiedSecret(key: string) {
    const stageId = unifiedPreview?.stage_id ?? null;
    try {
      await apiClient.post(`/api/apps/${appId}/secrets/generate`, {
        key,
        stage_id: stageId,
      });
      await loadStageSecrets();
      await loadUnifiedPreview();
    } catch (err) {
      reportErrorLocal('Failed to generate secret', err, setError, setDetailError);
      setStageSecretsResult(
        `Failed to generate ${key}: ${err instanceof Error ? err.message : String(err)}`
      );
    }
  }

  async function setUnifiedSecret(key: string, value: string) {
    const stageId = unifiedPreview?.stage_id ?? null;
    try {
      await apiClient.post(`/api/apps/${appId}/secrets`, {
        key,
        value,
        stage_id: stageId,
        secret_type: 'env',
        source_type: 'manual',
      });
      await loadStageSecrets();
      await loadUnifiedPreview();
    } catch (err) {
      reportErrorLocal('Failed to set secret', err, setError, setDetailError);
      setStageSecretsResult(
        `Failed to set ${key}: ${err instanceof Error ? err.message : String(err)}`
      );
    }
  }

  const loadGitlabInfo = useCallback(async (items: Conversation[], forceRefresh = false) => {
    const ids = items.slice(0, 50).map((conversation) => conversation.id);
    if (ids.length === 0) {
      return;
    }
    setGitlabRefreshBusy(true);
    try {
      const endpoint = forceRefresh
        ? '/api/conversations/gitlab?force_refresh=true'
        : '/api/conversations/gitlab';
      const data = await apiClient.post<{
        items: { conversation_id: string; gitlab: ConversationGitlabInfo | null }[];
      }>(endpoint, {
        conversation_ids: ids
      });
      const map = new Map(
        data.items.map((item) => [item.conversation_id, item.gitlab ?? null])
      );
      setConversations((prev) =>
        prev.map((conversation) =>
          map.has(conversation.id)
            ? { ...conversation, gitlab: map.get(conversation.id) ?? null }
            : conversation
        )
      );
    } catch (err) {
      reportErrorLocal('Failed to load GitLab status', err, setError, setDetailError);
    } finally {
      setGitlabRefreshBusy(false);
    }
  }, [apiClient, setError]);

  const loadConversations = useCallback(async () => {
    try {
      const data = await apiClient.get<Conversation[]>(`/api/apps/${appId}/conversations`);
      setConversations(data);
      void loadGitlabInfo(data);
    } catch (err) {
      reportErrorLocal('Failed to load conversations', err, setError, setDetailError);
    }
  }, [apiClient, appId, loadGitlabInfo, setError]);

  async function recalculateGitlabStatus() {
    await loadGitlabInfo(conversations, true);
  }

  const loadServers = useCallback(async () => {
    try {
      const data = await apiClient.get<Server[]>(`/api/servers`);
      setServers(data);
    } catch (err) {
      reportErrorLocal('Failed to load servers', err, setError, setDetailError);
    }
  }, [apiClient, setError]);

  const loadWorkerNodes = useCallback(async () => {
    try {
      const data = await apiClient.get<WorkerNode[]>('/api/executors/nodes');
      setWorkerNodes(Array.isArray(data) ? data : []);
    } catch (err) {
      setWorkerNodes([]);
      reportErrorLocal('Failed to load executors', err, setError, setDetailError);
    }
  }, [apiClient, setError]);

  const loadDomains = useCallback(async () => {
    try {
      const data = await apiClient.get<DnsDomainsResponse>('/api/dns/domains');
      setDnsDomains(data.domains ?? []);
      setDnsDefault(data.default_domain ?? null);
    } catch (err) {
      setDnsDomains([]);
      setDnsDefault(null);
      reportErrorLocal('Failed to load DNS domains', err, setError, setDetailError);
    }
  }, [apiClient, setError]);

  const loadStages = useCallback(async (preserveStageId?: string) => {
    try {
      const data = await apiClient.get<StageListResponse>(`/api/apps/${appId}/stages`);
      setStages((previousStages) => {
        if (!preserveStageId || data.stages.some((stage) => stage.id === preserveStageId)) {
          return data.stages;
        }
        const optimisticStage = previousStages.find((stage) => stage.id === preserveStageId);
        return optimisticStage ? [...data.stages, optimisticStage] : data.stages;
      });
    } catch (err) {
      if (!preserveStageId) {
        setStages([]);
      }
      reportErrorLocal('Failed to load stages', err, setError, setDetailError);
    }
  }, [apiClient, appId, setError]);

  const loadIdentityServices = useCallback(async (stageIdOverride?: string) => {
    try {
      const selectedStage = stageTabs.find((stage) => stage.id === selectedOpsStageId)?.stage ?? null;
      const stageId = stageIdOverride ?? selectedStage?.id;
      const params = stageId ? `?stage_id=${stageId}` : '';
      const data = await apiClient.get<ListIdentityServicesResponse>(
        `/api/apps/${appId}/identity/services${params}`
      );
      setIdentityServices(data.items);
      setYmlIdConfigured(data.yml_id_configured);
    } catch (err) {
      setIdentityServices([]);
      reportErrorLocal('Failed to load identity services', err, setError, setDetailError);
    }
  }, [apiClient, appId, setError, selectedOpsStageId, stageTabs]);

  useEffect(() => {
    if (!appId) {
      return;
    }
    setPipelineLimit(5);
    appGitlabRuntime.setPipelineLimit(5);
    appGitlabRuntime.clearWatchedPipelineJobs();
    setPipelineJobsOpen({});
    setPipelineJobsLoading({});
    let cancelled = false;
    (async () => {
      await loadDetail();
      if (cancelled) {
        return;
      }
      void loadPipelines(5);
      void loadConversations();
      void loadApps();
      void loadServers();
      void loadWorkerNodes();
      void loadDomains();
      void loadDeployKeyStatus();
      void loadOps();
      void loadStages();
      void loadAppAutopilot();
      void loadPipelineWaitSettings();
    })();
    setSelectedContainer('');
    setOpsResult(null);
    return () => {
      cancelled = true;
      appGitlabRuntime.clearWatchedPipelineJobs();
    };
  }, [
    appId,
    appGitlabRuntime,
    loadAppAutopilot,
    loadApps,
    loadConversations,
    loadDeployKeyStatus,
    loadDetail,
    loadDomains,
    loadOps,
    loadPipelineWaitSettings,
    loadPipelines,
    loadServers,
    loadWorkerNodes,
    loadStages
  ]);

  useEffect(() => {
    if (appId) {
      void loadStageSecrets();
    }
  }, [appId, loadStageSecrets]);

  useEffect(() => {
    if (!appId) {
      return;
    }
    void loadOpsHistory(selectedOpsStageId);
  }, [appId, loadOpsHistory, selectedOpsStageId]);

  useEffect(() => {
    if (!appId || stageTabs.length === 0) {
      return;
    }
    void loadIdentityServices();
  }, [appId, stageTabs, loadIdentityServices]);

  async function createStage(name: string, gitlabEnvironmentName?: string): Promise<boolean> {
    if (stageBusy) {
      return false;
    }
    setStageBusy(true);
    setStageResult(null);
    setStageError(null);
    try {
      const payload: { name: string; gitlab_environment_name?: string } = { name };
      if (gitlabEnvironmentName !== undefined) {
        payload.gitlab_environment_name = gitlabEnvironmentName;
      }
      const created = await apiClient.post<StageResponse>(`/api/apps/${appId}/stages`, payload);
      setStageResult(`Stage "${name}" created successfully.`);
      setStageError(null);
      setStages((previousStages) => {
        const existingStages = previousStages.filter((stage) => stage.id !== created.stage.id);
        return [...existingStages, created.stage];
      });
      setSelectedSettingsStageId(created.stage.id);
      setSelectedOpsStageId(created.stage.id);
      await loadStages(created.stage.id);
      return true;
    } catch (err) {
      setStageError(`Failed to create stage: ${err instanceof Error ? err.message : String(err)}`);
      return false;
    } finally {
      setStageBusy(false);
    }
  }

  async function deleteStage(stageId: string, stageName: string) {
    if (stageBusy) {
      return;
    }
    if (!confirm(`Are you sure you want to delete the stage "${stageName}"?`)) {
      return;
    }
    setStageBusy(true);
    setStageResult(null);
    setStageError(null);
    try {
      await apiClient.post<StageDeleteResponse>(`/api/apps/${appId}/stages/${stageId}/delete`, {});
      setStageResult(`Stage "${stageName}" deleted successfully.`);
      setStageError(null);
      await loadStages();
    } catch (err) {
      setStageError(`Failed to delete stage: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setStageBusy(false);
    }
  }

  async function updateStageSettings(
    stage: AppStage,
    values: {
      production_server_id: string | null;
      conversation_server_id: string | null;
      dns_record_name: string | null;
      gitlab_environment_name: string | null;
      app_dns_record_name?: string;
    }
  ) {
    if (stageBusy) {
      return;
    }
    setStageBusy(true);
    setStageResult(null);
    setStageError(null);
    try {
      await apiClient.post<StageResponse>(`/api/apps/${appId}/stages/${stage.id}`, {
        production_server_id: values.production_server_id,
        conversation_server_id: values.conversation_server_id,
        dns_record_name: values.dns_record_name,
        gitlab_environment_name: values.gitlab_environment_name,
      });
      if (stage.is_default) {
        const appPayload: Record<string, string> = {};
        if (values.app_dns_record_name) {
          appPayload.dns_record_name = values.app_dns_record_name;
        }
        if (Object.keys(appPayload).length > 0) {
          await apiClient.post(`/api/apps/${appId}`, appPayload);
        }
      }
      setStageResult(`Updated stage "${stage.name}" settings.`);
      await loadStages();
      await loadDetail();
    } catch (err) {
      setStageError(`Failed to update stage settings: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setStageBusy(false);
    }
  }

  async function updateAppTargets(values: {
    production_server_id: string | null;
    conversation_server_id: string | null;
    conversation_executor_policy: 'auto' | 'system';
  }) {
    if (targetBusy) {
      return;
    }
    setTargetBusy(true);
    setTargetResult(null);
    setTargetError(null);
    try {
      await apiClient.post(`/api/apps/${appId}`, {
        production_server_id: values.production_server_id,
        conversation_server_id: values.conversation_server_id,
        conversation_executor_policy: values.conversation_executor_policy,
        clear_production_server: values.production_server_id === null,
        clear_conversation_server: values.conversation_server_id === null,
        skip_cloudflare: true,
        skip_scripts: true
      });
      setTargetResult('Updated workspace targets.');
      await loadDetail();
      await loadStages();
    } catch (err) {
      setTargetError(`Failed to update workspace targets: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setTargetBusy(false);
    }
  }

  function workerCapabilityValue(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }

  function defaultWorkerRootForUser(user: string) {
    if (user === 'root') {
      return '/root/proj';
    }
    if (user === 'create_proj') {
      return '/create-proj/proj';
    }
    return `/home/${user}/proj`;
  }

  async function useWorkerTagForConversations(tag: string) {
    const workerTag = tag.trim();
    if (!workerTag || targetBusy) {
      return;
    }
    setTargetBusy(true);
    setTargetResult(null);
    setTargetError(null);
    try {
      const existing = servers.find((server) => {
        const capabilities = server.capabilities ?? {};
        const tags = Array.isArray(capabilities.worker_tags)
          ? capabilities.worker_tags.filter((value): value is string => typeof value === 'string')
          : [];
        return capabilities.transport === 'worker' && tags.includes(workerTag);
      });
      const liveNode = workerNodes.find((node) => (node.tags ?? []).includes(workerTag));
      const liveCapabilities = liveNode?.capabilities ?? {};
      const localUser = workerCapabilityValue(liveCapabilities.local_user) ?? 'root';
      const nativeRoot =
        workerCapabilityValue(liveCapabilities.native_root) ?? defaultWorkerRootForUser(localUser);
      const target = existing ?? await apiClient.post<Server>('/api/servers', {
        name: `Executor pool: ${workerTag}`,
        host: 'worker',
        ssh_user: localUser,
        ssh_port: 22,
        roles: ['conversation'],
        capabilities: {
          transport: 'worker',
          worker_tags: [workerTag],
          local_user: localUser,
          native_root: nativeRoot
        },
        risk_level: 'medium',
        enabled: true
      });
      await apiClient.post(`/api/apps/${appId}`, {
        conversation_server_id: target.id,
        conversation_executor_policy: 'auto',
        clear_conversation_server: false
      });
      setTargetResult(`Using executor pool "${workerTag}" for conversations.`);
      await loadServers();
      await loadWorkerNodes();
      await loadDetail();
      await loadStages();
    } catch (err) {
      setTargetError(`Failed to use executor pool: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setTargetBusy(false);
    }
  }

  async function ensureStageDomain(stage: AppStage) {
    if (stageBusy) {
      return;
    }
    setStageBusy(true);
    setStageResult(null);
    setStageError(null);
    try {
      const response = await apiClient.post<StageDnsEnsureResponse>(
        `/api/apps/${appId}/stages/${stage.id}/dns/ensure`,
        {}
      );
      setStageResult(`Reconciled DNS record "${response.record_name}"${response.target ? ` → ${response.target}` : ''}. HTTPS health has not been checked; no deployment or login settings changed.`);
      await loadStages();
      await loadDetail();
    } catch (err) {
      setStageError(`Failed to ensure DNS record: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setStageBusy(false);
    }
  }

  async function loadGitlabCiPreview() {
    if (gitlabCiBusy) {
      return;
    }
    setGitlabCiBusy(true);
    setGitlabCiPreview(null);
    setGitlabCiInstructions(null);
    try {
      const data = await apiClient.get<GitLabCiPreviewResponse>(`/api/apps/${appId}/stages/gitlab-ci/preview`);
      setGitlabCiPreview({ original: data.original_content, updated: data.updated_content });
      setGitlabCiInstructions(data.instructions ?? null);
      setGitlabCiPreviewOpen(true);
    } catch (err) {
      setError(`Failed to load GitLab CI preview: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setGitlabCiBusy(false);
    }
  }

  function deployWaitJobKey(pipelineId: number, jobId: number) {
    return `${pipelineId}:${jobId}`;
  }

  async function armDeployWait(pipeline: Pipeline, job?: PipelineJob) {
    if (!job) {
      return;
    }
    const key = deployWaitJobKey(pipeline.id, job.id);
    if (deployWaitBusyJobs[key]) {
      return;
    }
    setDeployWaitBusyJobs((prev) => ({ ...prev, [key]: true }));
    try {
      const data = await apiClient.post<{ status: string }>(
        `/api/apps/${appId}/pipelines/${pipeline.id}/deploy/auto`,
        {
          ref: pipeline.ref,
          job_id: job.id
        }
      );
      if (data.status === 'played') {
        setDeployResult(`Deploy job started (${job.name}) for pipeline #${pipeline.id}.`);
      } else {
        setDeployResult(`Deploy armed for ${job.name} in pipeline #${pipeline.id}.`);
      }
      loadPipelines();
      loadHookStatus();
      loadPipelineJobs(pipeline.id);
    } catch (err) {
      reportErrorLocal('Failed to arm deploy-if-green', err, setError, setDetailError);
    } finally {
      setDeployWaitBusyJobs((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  async function redeployPipeline(pipelineId: number) {
    if (redeployBusyId) {
      return;
    }
    setRedeployBusyId(pipelineId);
    try {
      const data = await apiClient.post<{ web_url?: string }>(
        `/api/apps/${appId}/pipelines/${pipelineId}/redeploy`
      );
      if (data.web_url) {
        setDeployResult(`Redeploy triggered (${data.web_url}).`);
      } else {
        setDeployResult(`Redeploy triggered for pipeline #${pipelineId}.`);
      }
      loadPipelines();
    } catch (err) {
      reportErrorLocal('Failed to redeploy pipeline', err, setError, setDetailError);
    } finally {
      setRedeployBusyId(null);
    }
  }

  async function cancelDeployWait(pipelineId: number, job: PipelineJob) {
    const key = deployWaitJobKey(pipelineId, job.id);
    if (deployWaitBusyJobs[key]) {
      return;
    }
    setDeployWaitBusyJobs((prev) => ({ ...prev, [key]: true }));
    try {
      await apiClient.del(
        `/api/apps/${appId}/pipelines/${pipelineId}/deploy/auto?job_id=${job.id}`
      );
      setDeployResult(`Deploy-if-green canceled for ${job.name} in pipeline #${pipelineId}.`);
      loadPipelines();
    } catch (err) {
      reportErrorLocal('Failed to cancel deploy-if-green', err, setError, setDetailError);
    } finally {
      setDeployWaitBusyJobs((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  function togglePipelineJobs(pipelineId: number) {
    const opening = !pipelineJobsOpen[pipelineId];
    setPipelineJobsOpen((prev) => ({
      ...prev,
      [pipelineId]: opening
    }));
    if (opening) {
      void loadPipelineJobs(pipelineId);
      return;
    }
    setPipelineJobsLoading((prev) => ({ ...prev, [pipelineId]: false }));
    appGitlabRuntime.watchPipelineJobs(pipelineId, false).catch((err) =>
      reportErrorLocal(
        'Failed to stop watching pipeline jobs',
        err,
        setError,
        setDetailError
      )
    );
  }

  async function restartContainers(container?: string) {
    try {
      const data = await apiClient.post<{ output: string }>(`/api/apps/${appId}/restart`, {
        container: container ?? null
      });
      setRestartResult(data.output || 'Restarted.');
    } catch (err) {
      reportErrorLocal('Failed to restart containers', err, setError, setDetailError);
    }
  }

  async function rotateSecret(secretKey: string) {
    if (opsBusyKey) {
      return;
    }
    setOpsBusyKey(secretKey);
    try {
      const data = await apiClient.post<{ key: string; restarted: string[] }>(
        `/api/apps/${appId}/ops/secrets/${encodeURIComponent(secretKey)}/rotate`
      );
      const restarts = data.restarted.length ? ` Restarted: ${data.restarted.join(', ')}.` : '';
      setOpsResult(`Rotated ${data.key}.${restarts}`);
      loadOps();
    } catch (err) {
      reportErrorLocal('Failed to rotate secret', err, setError, setDetailError);
    } finally {
      setOpsBusyKey(null);
    }
  }

  async function deleteConversation(conversationId: string) {
    if (!confirm('Are you sure you want to delete this conversation? This will also delete all messages, runs, and associated data.')) {
      return;
    }
    try {
      await apiClient.del(`/api/conversations/${conversationId}`);
      // Remove from local state
      setConversations(prev => prev.filter(c => c.id !== conversationId));
    } catch (err) {
      reportErrorLocal('Failed to delete conversation', err, setError, setDetailError);
    }
  }

  async function updateAppKey(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const gitlab_url = String(form.get('gitlab_url') ?? '').trim() || null;
    const gitlab_ref = String(form.get('gitlab_ref') ?? '').trim() || null;
    const slug = String(form.get('slug') ?? '').trim();
    const payload: Record<string, string | null> = {
      gitlab_url,
      gitlab_ref,
    };
    if (slug) {
      payload.slug = slug;
    }
    try {
      await apiClient.post(`/api/apps/${appId}`, payload);
      loadDetail();
      loadStages();
      loadDeployKeyStatus();
    } catch (err) {
      reportErrorLocal('Failed to update app', err, setError, setDetailError);
    }
  }

  async function updateSelfApp(next: boolean) {
    if (selfBusy) {
      return;
    }
    setSelfBusy(true);
    try {
      await apiClient.post(`/api/apps/${appId}`, { is_self: next });
      setSelfResult(next ? 'Marked as self app.' : 'Self app flag removed.');
      loadDetail();
    } catch (err) {
      reportErrorLocal('Failed to update self app flag', err, setError, setDetailError);
    } finally {
      setSelfBusy(false);
    }
  }

  async function updateTemplateApp(next: boolean) {
    if (templateBusy) {
      return;
    }
    setTemplateBusy(true);
    try {
      await apiClient.post(`/api/apps/${appId}`, { is_template: next });
      setTemplateResult(next ? 'Marked as template.' : 'Template flag removed.');
      loadDetail();
    } catch (err) {
      reportErrorLocal('Failed to update template flag', err, setError, setDetailError);
    } finally {
      setTemplateBusy(false);
    }
  }

  async function updateInfrastructureHelper(next: boolean) {
    if (infraHelperBusy) {
      return;
    }
    setInfraHelperBusy(true);
    setInfraHelperResult(null);
    try {
      await apiClient.post(`/api/apps/${appId}`, { infrastructure_helper: next });
      setInfraHelperResult(next ? 'Infrastructure helper enabled.' : 'Infrastructure helper disabled.');
      loadDetail();
    } catch (err) {
      reportErrorLocal('Failed to update infrastructure helper', err, setError, setDetailError);
    } finally {
      setInfraHelperBusy(false);
    }
  }

  async function approveInfrastructureCommandCatalog() {
    if (infraCatalogBusy) {
      return;
    }
    setInfraCatalogBusy(true);
    setInfraHelperResult(null);
    try {
      await apiClient.post(`/api/apps/${appId}/infrastructure-helper-catalog/approve`);
      setInfraHelperResult('Infrastructure command catalog approved.');
      loadDetail();
    } catch (err) {
      reportErrorLocal('Failed to approve infrastructure command catalog', err, setError, setDetailError);
    } finally {
      setInfraCatalogBusy(false);
    }
  }

  async function denyInfrastructureCommandCatalog() {
    if (infraCatalogBusy) {
      return;
    }
    setInfraCatalogBusy(true);
    setInfraHelperResult(null);
    try {
      await apiClient.post(`/api/apps/${appId}/infrastructure-helper-catalog/deny`);
      setInfraHelperResult('Infrastructure command catalog update dismissed.');
      loadDetail();
    } catch (err) {
      reportErrorLocal('Failed to dismiss infrastructure command catalog', err, setError, setDetailError);
    } finally {
      setInfraCatalogBusy(false);
    }
  }

  async function ensureDeployKey() {
    if (!deployKeyStatus?.can_fix || deployKeyBusy) {
      return;
    }
    setDeployKeyBusy(true);
    try {
      const data = await apiClient.post<DeployKeyStatus>(`/api/apps/${appId}/gitlab/deploy-key`);
      setDeployKeyStatus(data);
    } catch (err) {
      reportErrorLocal('Failed to add deploy key', err, setError, setDetailError);
    } finally {
      setDeployKeyBusy(false);
    }
  }

  async function saveAutofix() {
    try {
      await apiClient.post(`/api/apps/${appId}/autofix`, {
        enabled: autofixEnabled,
        only_default_branch: !autofixFeatureBranches,
        create_merge_request: autofixFeatureBranches,
        prompt_template: autofixPrompt
      });
      setAutofixResult('Autofix settings saved.');
      loadDetail();
      loadHookStatus();
    } catch (err) {
      reportErrorLocal('Failed to update autofix', err, setError, setDetailError);
    }
  }

  async function saveAppAutopilot() {
    try {
      await apiClient.patch<AppAutopilotSettings>(`/api/apps/${appId}/autopilot`, {
        default_mode: appAutopilotMode,
        enable_push_reminder: appEnablePushReminder,
        template_build_failed: appTemplateBuildFailed,
        template_merge_conflict: appTemplateMergeConflict,
        template_ready_semi: appTemplateReadySemi,
        template_ready_full: appTemplateReadyFull
      });
      setAppAutopilotResult('Conversation autopilot defaults saved.');
      await loadAppAutopilot();
    } catch (err) {
      reportErrorLocal('Failed to update app autopilot settings', err, setError, setDetailError);
    }
  }

  async function updatePipelineWaitAllowed(next: boolean) {
    if (pipelineWaitBusy) {
      return;
    }
    setPipelineWaitBusy(true);
    try {
      const data = await apiClient.patch<PipelineWaitSettings>(
        `/api/apps/${appId}/pipeline-wait-settings`,
        { allow_agent_pipeline_waits: next }
      );
      setPipelineWaitAllowed(data.allow_agent_pipeline_waits);
      setPipelineWaitResult('Pipeline wait setting saved.');
    } catch (err) {
      reportErrorLocal('Failed to update pipeline wait settings', err, setError, setDetailError);
    } finally {
      setPipelineWaitBusy(false);
    }
  }

  async function saveMrFeedback() {
    try {
      await apiClient.post(`/api/apps/${appId}/mr-feedback`, {
        auto_mr_feedback: autoMrFeedback
      });
      setMrFeedbackResult('MR feedback settings saved.');
      loadDetail();
    } catch (err) {
      reportErrorLocal('Failed to update MR feedback', err, setError, setDetailError);
    }
  }

  async function addGitlabHook() {
    if (hookBusy) {
      return;
    }
    setHookBusy(true);
    try {
      await apiClient.post(`/api/apps/${appId}/gitlab/hook`, {});
      setAutofixResult('GitLab webhook installed.');
      loadHookStatus();
    } catch (err) {
      reportErrorLocal('Failed to add GitLab webhook', err, setError, setDetailError);
    } finally {
      setHookBusy(false);
    }
  }

  async function removeGitlabHook() {
    if (hookBusy) {
      return;
    }
    setHookBusy(true);
    try {
      await apiClient.del(`/api/apps/${appId}/gitlab/hook`);
      setAutofixResult('GitLab webhook removed.');
      loadHookStatus();
    } catch (err) {
      reportErrorLocal('Failed to remove GitLab webhook', err, setError, setDetailError);
    } finally {
      setHookBusy(false);
    }
  }

  const reconcileIdentity = useCallback(async (serviceName: string) => {
    setIdentityServicesBusy(true);
    setIdentityResult(null);
    try {
      const selectedStage = stageTabs.find((stage) => stage.id === selectedOpsStageId)?.stage ?? null;
      const stageId = selectedStage?.id ?? undefined;
      await apiClient.post<ReconcileResult>(
        `/api/apps/${appId}/identity/services/${encodeURIComponent(serviceName)}/reconcile`,
        { stage_id: stageId ?? null }
      );
      setIdentityResult(`Identity service "${serviceName}" reconciled successfully.`);
      await loadIdentityServices(stageId);
      await loadStageSecrets();
      // The open preview contains its own identity validation snapshot. Refresh it
      // after reconciliation so a fixed identity is reflected without reopening
      // the dialog.
      if (unifiedPreviewOpen) {
        await loadUnifiedPreview();
      }
    } catch (err) {
      reportErrorLocal('Failed to reconcile identity service', err, setError, setDetailError);
      setIdentityResult(`Failed to reconcile identity service: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIdentityServicesBusy(false);
    }
  }, [
    apiClient,
    appId,
    loadIdentityServices,
    loadStageSecrets,
    loadUnifiedPreview,
    selectedOpsStageId,
    setError,
    stageTabs,
    unifiedPreviewOpen,
  ]);

  const checkIdentityHealth = useCallback(async () => {
    setIdentityHealthBusy(true);
    try {
      const data = await apiClient.get<IdentityHealthResponse>(
        `/api/apps/${appId}/identity/health`
      );
      setIdentityHealth(data);
    } catch (err) {
      setIdentityHealth({
        configured: false,
        error: `Health check failed: ${err instanceof Error ? err.message : String(err)}`,
      });
    } finally {
      setIdentityHealthBusy(false);
    }
  }, [apiClient, appId]);

  const deprovisionIdentity = useCallback(async (serviceName: string) => {
    if (!window.confirm(`Deprovision identity service "${serviceName}"? This will remove the client credentials.`)) {
      return;
    }
    setIdentityServicesBusy(true);
    setIdentityResult(null);
    try {
      const selectedStage = stageTabs.find((stage) => stage.id === selectedOpsStageId)?.stage ?? null;
      const stageId = selectedStage?.id ?? undefined;
      await apiClient.post<DeprovisionResult>(
        `/api/apps/${appId}/identity/services/${encodeURIComponent(serviceName)}/deprovision`,
        { stage_id: stageId ?? null }
      );
      setIdentityResult(`Identity service "${serviceName}" deprovisioned.`);
      await loadIdentityServices(stageId);
      await loadStageSecrets();
    } catch (err) {
      reportErrorLocal('Failed to deprovision identity service', err, setError, setDetailError);
      setIdentityResult(`Failed to deprovision identity service: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIdentityServicesBusy(false);
    }
  }, [apiClient, appId, loadIdentityServices, loadStageSecrets, selectedOpsStageId, setError, stageTabs]);

  const manualIdentitySecrets = useCallback(async (serviceName: string, clientId: string, clientSecret: string) => {
    setIdentityServicesBusy(true);
    setIdentityResult(null);
    try {
      const selectedStage = stageTabs.find((stage) => stage.id === selectedOpsStageId)?.stage ?? null;
      const stageId = selectedStage?.id;
      const secretSuffix = serviceName.replace(/[^a-zA-Z0-9_-]/g, '_').toUpperCase();
      // Store client_id as a stage secret
      await apiClient.post(`/api/apps/${appId}/secrets`, {
        key: `IDENTITY_${secretSuffix}_CLIENT_ID`,
        value: clientId,
        stage_id: stageId ?? undefined,
        secret_type: 'env',
        description: `Client ID for identity service ${serviceName}`,
      });
      // Store client_secret as a stage secret
      await apiClient.post(`/api/apps/${appId}/secrets`, {
        key: `IDENTITY_${secretSuffix}_CLIENT_SECRET`,
        value: clientSecret,
        stage_id: stageId ?? undefined,
        secret_type: 'env',
        description: `Client secret for identity service ${serviceName}`,
      });
      setIdentityResult(`Manual credentials saved for "${serviceName}".`);
      await loadIdentityServices(stageId);
      await loadStageSecrets();
    } catch (err) {
      reportErrorLocal('Failed to save manual identity secrets', err, setError, setDetailError);
      setIdentityResult(`Failed to save credentials: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIdentityServicesBusy(false);
    }
  }, [apiClient, appId, loadIdentityServices, loadStageSecrets, selectedOpsStageId, setError, stageTabs]);

  function resolveHookUrl(app: AppItem) {
    if (app.gitlab_url) {
      return `${app.gitlab_url.replace(/\/$/, '')}/-/hooks`;
    }
    if (app.gitlab_project_path) {
      return `https://gitlab.com/${app.gitlab_project_path}/-/hooks`;
    }
    return null;
  }

  function resetAutofixTemplate() {
    setAutofixPrompt(DEFAULT_AUTOFIX_TEMPLATE);
  }

  if (!detail) {
    return <div className="card">Loading app details...</div>;
  }

  const app = detail.app;
  const infrastructureCatalog = detail.infrastructure_command_catalog;
  const infrastructureCatalogActionable =
    infrastructureCatalog?.can_approve &&
    ['approval_required', 'update_available', 'denied'].includes(infrastructureCatalog.status);
  const containerOptions = app.container_names;
  const hasContainers = containerOptions.length > 0;
  const hookUrl = resolveHookUrl(app);
  const hookInstalled = hookStatus?.installed ?? null;
  const hookActive = hookStatus?.active ?? null;
  const hookWarnings = hookStatus?.warnings ?? [];
  const hookIncomplete = hookWarnings.some((warning) =>
    warning.toLowerCase().includes('missing mr/note events')
  );
  const hookDisabled = hookInstalled === true && hookActive === false;
  const hookNeedsRecreate = hookDisabled || hookIncomplete;
  const isPipelineFinished = (status: string) =>
    ['success', 'failed', 'canceled', 'skipped'].includes(status.trim().toLowerCase());
  const isManualJob = (job: PipelineJob) =>
    job.when?.toLowerCase() === 'manual' || job.status.toLowerCase() === 'manual';
  const isPendingJob = (job: PipelineJob) =>
    ['created', 'pending', 'scheduled', 'waiting_for_resource'].includes(
      job.status.toLowerCase()
    );
  const additionalStages = stages.filter((stage) => !stage.is_default);
  const resolveStageSnapshot = (stageId: string) => {
    const tab = stageTabs.find((stage) => stage.id === stageId) ?? stageTabs[0];
    const tabId = tab?.id ?? 'production';
    const stage = tab?.stage ?? null;
    const isDefault = tabId === 'production';
    const slugSuffix = stage?.slug_suffix || (isDefault ? '(production)' : '');
    const dnsRecord =
      stage?.dns_record_name ?? (isDefault ? app.dns_record_name ?? null : null);
    const containers =
      stage?.container_names?.length
        ? stage.container_names
        : isDefault
        ? app.container_names
        : [];
    const gitlabEnvironmentName = stage?.gitlab_environment_name ?? null;
    return {
      id: tabId,
      name: tab?.label ?? 'Production',
      isDefault,
      slugSuffix,
      dnsRecord,
      containers,
      gitlabEnvironmentName,
      stage
    };
  };
  const opsStageSnapshot = resolveStageSnapshot(selectedOpsStageId);
  const settingsScopeIsGeneral = selectedSettingsStageId === 'general';
  const settingsStageSnapshot = resolveStageSnapshot(
    settingsScopeIsGeneral ? 'production' : selectedSettingsStageId
  );
  const settingsSecretScope = resolveSecretScopeSelection();
  const settingsSecretScopeLabel =
    settingsSecretScope.scope === 'stage'
      ? `Stage override (${settingsStageSnapshot.name})`
      : settingsScopeIsGeneral
      ? 'General (inherited by all stages)'
      : 'General (inherited by production)';
  const productionStageDnsRecord =
    stages.find((stage) => stage.is_default)?.dns_record_name ?? app.dns_record_name ?? null;
  const manifestSecrets = opsOverview?.secrets ?? [];
  const usedStageSecretKeys = new Set(stageSecrets.map((secret) => secret.key));
  const availableManifestSecrets = manifestSecrets.filter(
    (secret) => !usedStageSecretKeys.has(secret.key) || secret.key === newSecretKey
  );
  const effectiveNewSecretKey = newSecretKey || availableManifestSecrets[0]?.key || '';
  const selectedNewSecretSpec =
    availableManifestSecrets.find((secret) => secret.key === effectiveNewSecretKey) ?? null;
  const selectedNewSecretType = (selectedNewSecretSpec?.secret_type ?? newSecretType) as
    | 'env'
    | 'file';
  const selectedManifestFilePath = (selectedNewSecretSpec?.file_path ?? '').trim();
  const operationsView = (
    <AppDetailOperationsView
      deployResult={deployResult}
      pipelineError={pipelineError}
      pipelines={pipelines}
      pipelineJobsOpen={pipelineJobsOpen}
      pipelineJobsLoading={pipelineJobsLoading}
      pipelineJobs={pipelineJobs}
      deployWaitBusyJobs={deployWaitBusyJobs}
      redeployBusyId={redeployBusyId}
      onRefreshPipelines={loadPipelines}
      onTogglePipelineJobs={togglePipelineJobs}
      onRedeployPipeline={redeployPipeline}
      isPipelineFinished={isPipelineFinished}
      isManualJob={isManualJob}
      isPendingJob={isPendingJob}
      deployWaitJobKey={deployWaitJobKey}
      onCancelDeployWait={(pipelineId, job) => {
        void cancelDeployWait(pipelineId, job);
      }}
      onArmDeployWait={(pipeline, job) => {
        void armDeployWait(pipeline, job);
      }}
      onLoadMorePipelines={loadMorePipelines}
      unifiedPreviewBusy={unifiedPreviewBusy}
      onLoadUnifiedPreview={loadUnifiedPreview}
      stageTabs={stageTabs}
      selectedOpsStageId={selectedOpsStageId}
      onSelectOpsStageId={setSelectedOpsStageId}
      opsStageSnapshot={opsStageSnapshot}
      opsOverview={opsOverview}
      opsResult={opsResult}
      opsHistory={opsHistory}
      opsHistoryBusy={opsHistoryBusy}
      opsHistoryResult={opsHistoryResult}
      opsRevertBusyCommit={opsRevertBusyCommit}
      onRefreshOpsHistory={() => {
        void loadOpsHistory();
      }}
      onRevertOpsCommit={(commit) => {
        void revertOpsCommit(commit);
      }}
      gitlabRefreshBusy={gitlabRefreshBusy}
      onRecalculateGitlabStatus={() => {
        void recalculateGitlabStatus();
      }}
      onLoadConversations={loadConversations}
      apiClient={apiClient}
      availableApps={availableApps}
      appId={appId}
      onConversationCreated={(conversationId) => navigate(`/conversations/${conversationId}`)}
      setError={setError}
      conversations={conversations}
      onDeleteConversation={(conversationId) => {
        void deleteConversation(conversationId);
      }}
    />
  );

  const settingsView = (<>
    <StageMigrationPanel apiClient={apiClient} appId={appId} stages={stages} executors={workerNodes} />
    {settingsScopeIsGeneral && (
      <AppExecutorPolicyPanel
        apiClient={apiClient}
        appId={appId}
        executors={workerNodes}
        setError={setError}
      />
    )}
    <AppDetailSettingsView
      apiClient={apiClient}
      appId={appId}
      app={app}
      detail={detail}
      stages={stages}
      servers={servers}
      workerNodes={workerNodes}
      dnsDomains={dnsDomains}
      dnsDefault={dnsDefault}
      settingsScopeTabs={settingsScopeTabs}
      selectedSettingsStageId={selectedSettingsStageId}
      selectedContainer={selectedContainer}
      hasContainers={hasContainers}
      hookUrl={hookUrl}
      hookStatus={hookStatus}
      hookInstalled={hookInstalled}
      hookDisabled={hookDisabled}
      hookIncomplete={hookIncomplete}
      hookNeedsRecreate={hookNeedsRecreate}
      hookBusy={hookBusy}
      deployKeyStatus={deployKeyStatus}
      deployKeyBusy={deployKeyBusy}
      gitlabCiBusy={gitlabCiBusy}
      selfBusy={selfBusy}
      templateBusy={templateBusy}
      infraHelperBusy={infraHelperBusy}
      infraCatalogBusy={infraCatalogBusy}
      settingsScopeIsGeneral={settingsScopeIsGeneral}
      settingsStageSnapshot={settingsStageSnapshot}
      settingsSecretScope={settingsSecretScope}
      settingsSecretScopeLabel={settingsSecretScopeLabel}
      additionalStages={additionalStages}
      opsOverview={opsOverview}
      opsResult={opsResult}
      opsBusyKey={opsBusyKey}
      stageBusy={stageBusy}
      stageResult={stageResult}
      stageError={stageError}
      targetBusy={targetBusy}
      targetResult={targetResult}
      targetError={targetError}
      stageSecrets={stageSecrets}
      availableApps={availableApps}
      stageSecretsBusy={stageSecretsBusy}
      stageSecretsResult={stageSecretsResult}
      manifestSecrets={manifestSecrets}
      availableManifestSecrets={availableManifestSecrets}
      effectiveNewSecretKey={effectiveNewSecretKey}
      selectedNewSecretSpec={selectedNewSecretSpec}
      selectedNewSecretType={selectedNewSecretType}
      selectedManifestFilePath={selectedManifestFilePath}
      newSecretDescription={newSecretDescription}
      newSecretValue={newSecretValue}
      newSecretSourceType={newSecretSourceType}
      newExternalAppId={newExternalAppId}
      newExternalStageMode={newExternalStageMode}
      newExternalStageId={newExternalStageId}
      newExternalSecretKey={newExternalSecretKey}
      editingSecretId={editingSecretId}
      editSecretValue={editSecretValue}
      editSecretSourceType={editSecretSourceType}
      editExternalAppId={editExternalAppId}
      editExternalStageMode={editExternalStageMode}
      editExternalStageId={editExternalStageId}
      editExternalSecretKey={editExternalSecretKey}
      unifiedPreviewBusy={unifiedPreviewBusy}
      autofixEnabled={autofixEnabled}
      autofixFeatureBranches={autofixFeatureBranches}
      autofixPrompt={autofixPrompt}
      autofixResult={autofixResult}
      appAutopilotMode={appAutopilotMode}
      appEnablePushReminder={appEnablePushReminder}
      appTemplateBuildFailed={appTemplateBuildFailed}
      appTemplateMergeConflict={appTemplateMergeConflict}
      appTemplateReadySemi={appTemplateReadySemi}
      appTemplateReadyFull={appTemplateReadyFull}
      appAutopilotResult={appAutopilotResult}
      pipelineWaitAllowed={pipelineWaitAllowed}
      pipelineWaitBusy={pipelineWaitBusy}
      pipelineWaitResult={pipelineWaitResult}
      autoMrFeedback={autoMrFeedback}
      mrFeedbackResult={mrFeedbackResult}
      restartResult={restartResult}
      selfResult={selfResult}
      templateResult={templateResult}
      infraHelperResult={infraHelperResult}
      backgroundJobResult={backgroundJobResult}
      onSetError={setError}
      onSelectSettingsScope={(id) => {
        setSelectedSettingsStageId(id);
        if (id !== 'general') {
          setSelectedOpsStageId(id);
        }
      }}
      onOpenStageCreateDialog={() => setStageCreateDialogOpen(true)}
      onRefreshStages={() => {
        void loadStages();
      }}
      onUpdateAppKey={updateAppKey}
      onEnsureDeployKey={ensureDeployKey}
      onUpdateSelfApp={updateSelfApp}
      onUpdateTemplateApp={updateTemplateApp}
      onUpdateInfrastructureHelper={updateInfrastructureHelper}
      onApproveInfrastructureCommandCatalog={approveInfrastructureCommandCatalog}
      onDenyInfrastructureCommandCatalog={denyInfrastructureCommandCatalog}
      onLoadGitlabCiPreview={loadGitlabCiPreview}
      onDeleteStage={deleteStage}
      onEnsureStageDomain={ensureStageDomain}
      onUpdateStageSettings={updateStageSettings}
      onUpdateAppTargets={updateAppTargets}
      onUseWorkerTagForConversations={useWorkerTagForConversations}
      onRotateSecret={rotateSecret}
      onDeleteStageSecret={deleteStageSecret}
      onUpdateStageSecret={updateStageSecret}
      onCreateStageSecret={createStageSecret}
      onMoveStageSecret={moveStageSecret}
      onSetEditingSecretId={setEditingSecretId}
      onSetEditSecretValue={setEditSecretValue}
      onSetEditSecretSourceType={setEditSecretSourceType}
      onSetEditExternalAppId={setEditExternalAppId}
      onSetEditExternalStageMode={setEditExternalStageMode}
      onSetEditExternalStageId={setEditExternalStageId}
      onSetEditExternalSecretKey={setEditExternalSecretKey}
      onSetNewSecretKey={setNewSecretKey}
      onSetNewSecretType={setNewSecretType}
      onSetNewSecretDescription={setNewSecretDescription}
      onSetNewSecretValue={setNewSecretValue}
      onSetNewSecretSourceType={setNewSecretSourceType}
      onSetNewExternalAppId={setNewExternalAppId}
      onSetNewExternalStageMode={setNewExternalStageMode}
      onSetNewExternalStageId={setNewExternalStageId}
      onSetNewExternalSecretKey={setNewExternalSecretKey}
      onLoadUnifiedPreview={loadUnifiedPreview}
      onSetAutofixEnabled={setAutofixEnabled}
      onSetAutofixFeatureBranches={setAutofixFeatureBranches}
      onSetAutofixPrompt={setAutofixPrompt}
      onSaveAutofix={saveAutofix}
      onResetAutofixTemplate={resetAutofixTemplate}
      onSetAppAutopilotMode={setAppAutopilotMode}
      onSetAppEnablePushReminder={setAppEnablePushReminder}
      onSetAppTemplateBuildFailed={setAppTemplateBuildFailed}
      onSetAppTemplateMergeConflict={setAppTemplateMergeConflict}
      onSetAppTemplateReadySemi={setAppTemplateReadySemi}
      onSetAppTemplateReadyFull={setAppTemplateReadyFull}
      onSaveAppAutopilot={saveAppAutopilot}
      onUpdatePipelineWaitAllowed={updatePipelineWaitAllowed}
      onSetAutoMrFeedback={setAutoMrFeedback}
      onSaveMrFeedback={saveMrFeedback}
      onAddGitlabHook={addGitlabHook}
      onRemoveGitlabHook={removeGitlabHook}
      onSetSelectedContainer={setSelectedContainer}
      onRestartContainers={restartContainers}
      onSetBackgroundJobResult={setBackgroundJobResult}
      onDismissStageError={() => setStageError(null)}
      onDismissTargetError={() => setTargetError(null)}
      identityServices={identityServices}
      identityServicesBusy={identityServicesBusy}
      identityResult={identityResult}
      ymlIdConfigured={ymlIdConfigured}
      identityHealth={identityHealth}
      identityHealthBusy={identityHealthBusy}
      onReconcileIdentity={reconcileIdentity}
      onDeprovisionIdentity={deprovisionIdentity}
      onManualIdentitySecrets={manualIdentitySecrets}
      onCheckIdentityHealth={checkIdentityHealth}
    />
  </>
  );

  return (
    <>
      <section className="grid">
        <div className="app-detail-page app-detail-overview45">
          <div className="card-header app-detail-page__header app-detail-overview45__header">
            <div className="app-detail-overview45__identity">
              <p className="app-detail-overview45__eyebrow">App overview</p>
              <h2>{app.name}</h2>
            </div>
            <div className="card-actions">
              {showMembersLink && (
                <Link to={`/apps/${appId}/members`} className="link-button">
                  Members
                </Link>
              )}
              <Link to={`/apps/${appId}/debugging`} className="link-button">
                Debugging
              </Link>
            </div>
          </div>
          {detailError && (
            <ErrorBanner message={detailError} onDismiss={() => setDetailError(null)} />
          )}
          {gitlabUpdates.failure && (
            <GitlabUpdatesNotice failure={gitlabUpdates.failure} onRetry={gitlabUpdates.retry} />
          )}
          <dl className="app-detail-overview45__facts" aria-label="App overview">
              <div>
                <dt>GitLab</dt>
                <dd>{app.gitlab_project_path ?? app.gitlab_url ?? '—'}</dd>
              </div>
              <div>
                <dt>DNS record</dt>
                <dd>{productionStageDnsRecord ?? '—'}</dd>
              </div>
              <div>
                <dt>Production server</dt>
                <dd>{detail.production_server?.name ?? '—'}</dd>
              </div>
              <div>
                <dt>Conversation server</dt>
                <dd>{detail.conversation_server?.name ?? '—'}</dd>
              </div>
          </dl>

          {infrastructureCatalogActionable && infrastructureCatalog && (
            <div className="status">
              <strong>Infrastructure command catalog</strong>
              <p>{infrastructureCatalog.message}</p>
              <div className="row">
                <span>Approved</span>
                <span className="mono">
                  {infrastructureCatalog.approved_git_ref?.slice(0, 12) ?? '—'}
                </span>
              </div>
              <div className="row">
                <span>Current</span>
                <span className="mono">
                  {infrastructureCatalog.current_git_ref?.slice(0, 12) ?? '—'}
                </span>
              </div>
              <div className="row">
                <button
                  type="button"
                  className="ghost"
                  onClick={() => void approveInfrastructureCommandCatalog()}
                  disabled={infraCatalogBusy}
                >
                  {infraCatalogBusy ? 'Working...' : 'Approve current command catalog'}
                </button>
                {infrastructureCatalog.status !== 'denied' && (
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => void denyInfrastructureCommandCatalog()}
                    disabled={infraCatalogBusy}
                  >
                    Dismiss current update
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="tabs app-detail-overview45__sections" role="group" aria-label="App sections">
            <button
              type="button"
              className={activeView === 'operations' ? 'active' : ''}
              aria-pressed={activeView === 'operations'}
              onClick={() => setActiveView('operations')}
            >
              Operations
            </button>
            <button
              type="button"
              className={activeView === 'settings' ? 'active' : ''}
              aria-pressed={activeView === 'settings'}
              onClick={() => setActiveView('settings')}
            >
              Settings
            </button>
          </div>

          {activeView === 'operations' ? operationsView : settingsView}
        </div>
      </section>

    <GitlabCiPreviewDialog
      open={gitlabCiPreviewOpen}
      preview={gitlabCiPreview}
      instructions={gitlabCiInstructions}
      onClose={() => setGitlabCiPreviewOpen(false)}
    />
    <UnifiedOpsPreviewDialog
      open={unifiedPreviewOpen}
      preview={unifiedPreview}
      selectedFiles={selectedPreviewFiles}
      applying={unifiedApplyBusy}
      hasMissingSecrets={hasUnifiedMissingSecrets}
      missingSecretKeys={unifiedMissingSecretKeys}
      identityServices={identityServices}
      identityServicesBusy={identityServicesBusy}
      ymlIdConfigured={ymlIdConfigured}
      identityHealth={identityHealth}
      identityHealthBusy={identityHealthBusy}
      onClose={() => setUnifiedPreviewOpen(false)}
      onApply={applyUnifiedPreview}
      onToggleAll={(checked) => {
        if (checked) {
          setSelectedPreviewFiles(new Set(unifiedPreview?.files?.map((file) => file.path) ?? []));
          return;
        }
        setSelectedPreviewFiles(new Set());
      }}
      onToggleFile={(path, checked) => {
        setSelectedPreviewFiles((current) => {
          const next = new Set(current);
          if (checked) {
            next.add(path);
          } else {
            next.delete(path);
          }
          return next;
        });
      }}
      onGenerateMissing={generateMissingUnifiedSecrets}
      onImportDeployedSecrets={importDeployedSecrets}
      onGenerateSecret={generateUnifiedSecret}
      onSetSecret={setUnifiedSecret}
      onReconcileIdentity={reconcileIdentity}
      onManualIdentitySecrets={manualIdentitySecrets}
      onCheckIdentityHealth={checkIdentityHealth}
    />
	    <StageCreateDialog
        open={stageCreateDialogOpen}
        disabled={stageBusy}
        onClose={() => setStageCreateDialogOpen(false)}
        onCreate={async (name, gitlabEnvironmentName) => {
          const created = await createStage(name, gitlabEnvironmentName);
          if (created) {
            setStageCreateDialogOpen(false);
          }
          return created;
        }}
      />
	  </>
	  );
}
